import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  type = 'website',
  image = '/og-image.jpg',
  noindex = false
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const baseUrl = 'https://www.opteam-services.com';
  const fullUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const fullImage = image.startsWith('http') ? image : `${baseUrl}${image}`;

  const defaultTitle = currentLang === 'fr'
    ? 'OPTEAM Services - RPA, IA & Low-Code | Automatisation Intelligente'
    : 'OPTEAM Services - RPA, AI & Low-Code | Intelligent Automation';

  const defaultDescription = currentLang === 'fr'
    ? 'OPTEAM Services : expert en automatisation RPA, Intelligence Artificielle et Low-Code. Transformez vos processus, réduisez vos coûts et boostez votre productivité.'
    : 'OPTEAM Services: RPA automation, Artificial Intelligence and Low-Code experts. Transform your processes, reduce costs and boost productivity.';

  const defaultKeywords = currentLang === 'fr'
    ? 'RPA, automatisation, robotique, low-code, intelligence artificielle, IA, UiPath, Blue Prism, Automation Anywhere, transformation digitale, processus métier, France, Paris'
    : 'RPA, automation, robotics, low-code, artificial intelligence, AI, UiPath, Blue Prism, Automation Anywhere, digital transformation, business processes, France, Paris';

  const finalTitle = title ? `${title} | OPTEAM Services` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;

  // Structured data for Organization
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'OPTEAM Services',
    url: baseUrl,
    logo: `${baseUrl}/logo192.png`,
    description: finalDescription,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+33-6-38-83-17-50',
      contactType: 'customer service',
      availableLanguage: ['French', 'English']
    },
    sameAs: [
      'https://linkedin.com/company/opteam-services',
      'https://twitter.com/opteamservices'
    ]
  };

  // Structured data for LocalBusiness
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'OPTEAM Services',
    image: `${baseUrl}/logo192.png`,
    url: baseUrl,
    telephone: '+33-6-38-83-17-50',
    email: 'contact@opteam-services.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR'
    },
    priceRange: '$$',
    serviceType: ['RPA Automation', 'AI Solutions', 'Low-Code Development', 'Digital Transformation'],
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 48.8566,
        longitude: 2.3522
      },
      geoRadius: '500000'
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="OPTEAM Services" />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={fullUrl} />

      {/* Language Alternates */}
      <link rel="alternate" hreflang="fr" href={`${baseUrl}${canonical || '/'}`} />
      <link rel="alternate" hreflang="en" href={`${baseUrl}${canonical || '/'}`} />
      <link rel="alternate" hreflang="x-default" href={`${baseUrl}${canonical || '/'}`} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="OPTEAM Services" />
      <meta property="og:locale" content={currentLang === 'fr' ? 'fr_FR' : 'en_US'} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content="@opteamservices" />

      {/* Additional SEO Meta Tags */}
      <meta name="geo.region" content="FR" />
      <meta name="geo.placename" content="Paris" />
      <meta name="format-detection" content="telephone=yes" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
